"use client"

import { CommonStylesProps } from "@/(admin)/modules/ui-builder/config/lib/commonCSSProps"
import { generateResponsiveCSS } from "@/(admin)/modules/ui-builder/config/lib/helper"
import { ADMIN_API_CONFIG } from "@/constants/api-configs"
import { t } from "@/languages/i18n"
import { ComponentConfig } from "@measured/puck"
import Image from "next/image"
import { useRouter } from "next/navigation"

export interface FooterProps extends CommonStylesProps {
  logoImage: string
  servicesItems: {
    title: string
    url: string
  }[]
  contactInfoItems: {
    url: string
    contactInfo: string
    isGmail: string
    iconUrl: string
  }[]
  iconList: {
    icon: string
    url: string
  }[]
}

export const RenderConfig: ComponentConfig<FooterProps> = {
  render: ({ logoImage, servicesItems, contactInfoItems, iconList, className, styles, responsiveType }) => {
    const id = `navigation-${Math.random().toString(36).substr(2, 9)}`
    const router = useRouter()
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)

    return (
      <>
        {responsiveCSS}
        <footer className="bg-[#091A25] rounded-t-[64px] md:rounded-t-[64px] px-4 lg:px-[120px] w-full">
          <div className="max-w-[1200px] mx-auto py-8 md:py-16">
            <div className="flex flex-col md:flex-row gap-8 lg:gap-[120px] justify-between items-start">
              {/* Logo and Social Icons */}
              <div className="flex flex-col gap-6 w-full md:w-auto">
                <Image
                  src={`${ADMIN_API_CONFIG.API_URL}${logoImage}`}
                  alt="logo"
                  width={141}
                  height={50}
                  className="max-w-[120px] md:max-w-[141px] max-h-[43px] md:max-h-[50px] object-cover hover:!opacity-80 transition-all cursor-pointer"
                  onClick={() => {
                    router.push("/")
                  }}
                />
                <div className="flex flex-row gap-3 items-start">
                  {iconList.map((item, index) => (
                    <a
                      key={index}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="!text-white hover:!opacity-80 transition-all"
                    >
                      <Image src={`${ADMIN_API_CONFIG.API_URL}${item.icon}`} alt="icon" width={24} height={24} />
                    </a>
                  ))}
                </div>
              </div>

              {/* Services */}
              <div className="flex flex-col gap-4 md:gap-6 w-full md:w-auto">
                <h3 className="text-white font-semibold text-lg md:text-xl leading-[27px] md:leading-[30px]">
                  {t("Services")}
                </h3>
                <div className="grid grid-cols-2 gap-x-3 gap-y-4 md:gap-x-[144px] md:gap-y-3">
                  {servicesItems.map((item, index) => (
                    <a
                      key={index}
                      href={item.url}
                      className="!text-[#FFFFFF80] hover:!text-white transition-all font-normal text-sm md:text-base leading-[22px] md:leading-[26px] md:text-nowrap"
                    >
                      {item.title}
                    </a>
                  ))}
                </div>
              </div>

              {/* Contact */}
              <div className="flex flex-col gap-4 md:gap-6 w-full md:w-auto">
                <h3 className="text-white font-semibold text-lg md:text-xl leading-[27px] md:leading-[30px]">
                  {t("Contact")}
                </h3>
                <div className="flex flex-col gap-3">
                  {contactInfoItems.map((item, index) => (
                    <a
                      key={index}
                      href={item.url}
                      className="flex items-center gap-2 !text-[#FFFFFF80] hover:!text-white transition-all font-normal text-sm md:text-base leading-[22px] md:leading-[26px] lg:text-nowrap break-words"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Image
                        src={`${ADMIN_API_CONFIG.API_URL}${item.iconUrl}`}
                        alt="contact icon"
                        width={20}
                        height={20}
                        className="flex-shrink-0"
                      />
                      <span className="break-all md:break-normal">{item.contactInfo}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </footer>
      </>
    )
  },
}

export default RenderConfig
